import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@common-fe/common-fe';

import { QUERY_KEY as PROCESSING_RULE_BY_ID_QUERY_KEY } from '@/modules/ProcessingDefinition/ProcessingRule/queries/useGetProcessingRuleById.query';

interface UseProcessingRuleModalProps {
  currentProcessingRuleName?: string;
  processingRuleSourceId?: string;
  onStateChange: (state: { processingRuleName: string }) => void;
  onFormKeyChange: () => void;
  onResetPagination?: () => void;
}

export const useProcessingRuleModal = ({
  currentProcessingRuleName,
  processingRuleSourceId,
  onStateChange,
  onFormKeyChange,
  onResetPagination,
}: UseProcessingRuleModalProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string>();
  const [isCreatingNewRule, setIsCreatingNewRule] = useState(false);
  const [previousProcessingRuleName, setPreviousProcessingRuleName] = useState<string>();
  const ruleWasCreatedRef = useRef(false);
  const queryClient = useQueryClient();

  const openModalForCreate = useCallback(() => {
    setPreviousProcessingRuleName(currentProcessingRuleName);
    setIsCreatingNewRule(true);
    setIsModalVisible(true);
  }, [currentProcessingRuleName]);

  const openModalForEdit = useCallback(() => {
    if (currentProcessingRuleName) {
      setEditingRuleId(currentProcessingRuleName);
      setIsModalVisible(true);
    }
  }, [currentProcessingRuleName]);

  const handleModalClose = useCallback(
    (visible: boolean) => {
      setIsModalVisible(visible);

      if (!visible) {
        setEditingRuleId(undefined);

        // Restore previous value ONLY if user cancelled (rule wasn't created)
        if (isCreatingNewRule && previousProcessingRuleName !== undefined && !ruleWasCreatedRef.current) {
          onStateChange({ processingRuleName: previousProcessingRuleName });
          onFormKeyChange();
        }

        // Clear flags
        setPreviousProcessingRuleName(undefined);
        setIsCreatingNewRule(false);
        ruleWasCreatedRef.current = false;
      }
    },
    [isCreatingNewRule, previousProcessingRuleName, onStateChange, onFormKeyChange]
  );

  const handleRuleCreated = useCallback(
    (ruleId?: string) => {
      if (ruleId) {
        ruleWasCreatedRef.current = true;
        onStateChange({ processingRuleName: ruleId });
        onFormKeyChange();

        // Reset pagination to first page when creating new rule
        if (isCreatingNewRule && onResetPagination) {
          onResetPagination();
        }

        // Invalidate query for source rule to refetch updated data when editing existing rule
        // Check if we're editing (not creating new) by comparing ruleId with source rule
        if (processingRuleSourceId && ruleId === processingRuleSourceId) {
          queryClient.invalidateQueries({
            queryKey: [PROCESSING_RULE_BY_ID_QUERY_KEY, processingRuleSourceId],
          });
        }
      }
    },
    [
      onStateChange,
      onFormKeyChange,
      isCreatingNewRule,
      onResetPagination,
      processingRuleSourceId,
      queryClient,
    ]
  );

  return {
    isModalVisible,
    editingRuleId,
    openModalForCreate,
    openModalForEdit,
    handleModalClose,
    handleRuleCreated,
  };
};
